/**
 * Simple Markdown-like parser for forum posts
 * Supports: bold, italic, links, lists, inline code, colored text, inline images, and entity embeds
 * Safe for rendering (no script injection)
 */

// Entity link regex: [[type:uuid]]
const ENTITY_LINK_REGEX = /\[\[(species|report|trip|thread):([a-f0-9-]+)\]\]/g;

// Escape HTML entities to prevent XSS
const escapeHtml = (text) => {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
};

// Allowed colors for safety
const ALLOWED_COLORS = [
  'red', 'blue', 'green', 'orange', 'purple', 'pink', 'cyan', 'teal',
  'yellow', 'gray', 'black', 'white', 'brown', 'navy', 'lime', 'gold'
];

/**
 * Parse simple markdown to HTML
 * Supports:
 * - **bold** or __bold__
 * - *italic* or _italic_
 * - [link text](url)
 * - `inline code`
 * - {color:red}text{/color} for colored text
 * - ![alt](imageUrl) or ![alt](imageUrl|left/center/right) for inline images
 * - Lists starting with - or *
 * - Line breaks
 */
export const parseSimpleMarkdown = (text, images = []) => {
  if (!text) return '';
  
  // First escape HTML to prevent XSS
  let html = escapeHtml(text);
  
  // Inline images with position: ![alt](url) or ![alt](url|position)
  // Also support image index: ![alt](#0) for first uploaded image, ![alt](#1|center) for second with center position
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, urlAndPos) => {
    let url = urlAndPos;
    let position = 'left'; // default
    
    // Check if position is specified
    if (urlAndPos.includes('|')) {
      const parts = urlAndPos.split('|');
      url = parts[0];
      const pos = parts[1]?.toLowerCase();
      if (['left', 'center', 'right'].includes(pos)) {
        position = pos;
      }
    }
    
    // Check if it's an image index reference (#0, #1, etc.)
    if (url.startsWith('#') && images.length > 0) {
      const index = parseInt(url.slice(1), 10);
      if (!isNaN(index) && index >= 0 && index < images.length) {
        url = images[index];
      } else {
        return match; // Invalid index, return original
      }
    }
    
    // Only allow certain URL patterns for security
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/api/')) {
      return match;
    }
    
    const alignClass = position === 'center' 
      ? 'mx-auto' 
      : position === 'right' 
        ? 'ml-auto' 
        : 'mr-auto';
    
    return `<div class="my-2 ${alignClass}" style="max-width: 200px;">
      <img src="${url}" alt="${alt}" class="rounded-lg max-h-40 object-cover cursor-pointer hover:opacity-90 w-full" onclick="window.open('${url}', '_blank')" />
    </div>`;
  });
  
  // Colored text: {color:red}text{/color}
  html = html.replace(/\{color:(\w+)\}(.+?)\{\/color\}/g, (match, color, content) => {
    // Only allow safe color names
    if (ALLOWED_COLORS.includes(color.toLowerCase())) {
      return `<span style="color: ${color.toLowerCase()}">${content}</span>`;
    }
    return content; // Return just the content if color is not allowed
  });
  
  // Entity links: [[species:uuid]], [[report:uuid]], [[trip:uuid]], [[thread:uuid]]
  // Renders as a placeholder div that React will hydrate with EntityMiniCard
  html = html.replace(/\[\[(species|report|trip|thread):([a-f0-9-]+)\]\]/g, (match, type, id) => {
    return `<span data-entity-type="${type}" data-entity-id="${id}" class="entity-embed-placeholder inline-block align-middle"></span>`;
  });

  // Bold: **text** or __text__
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  
  // Italic: *text* or _text_ (but not inside words)
  html = html.replace(/(?<!\w)\*([^*]+)\*(?!\w)/g, '<em>$1</em>');
  html = html.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');
  
  // Inline code: `code`
  html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-sm font-mono">$1</code>');
  
  // Links: [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
    // Only allow http/https URLs
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-cyan-600 hover:text-cyan-700 underline">${text}</a>`;
    }
    return match; // Return original if not a valid URL
  });
  
  // Process lists and line breaks
  const lines = html.split('\n');
  let inList = false;
  const processedLines = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isListItem = /^[\s]*[-*]\s+(.+)$/.exec(line);
    
    if (isListItem) {
      if (!inList) {
        processedLines.push('<ul class="list-disc list-inside my-2 space-y-1">');
        inList = true;
      }
      processedLines.push(`<li>${isListItem[1]}</li>`);
    } else {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      if (line.trim()) {
        processedLines.push(`<p class="mb-2">${line}</p>`);
      } else {
        processedLines.push('<br/>');
      }
    }
  }
  
  if (inList) {
    processedLines.push('</ul>');
  }
  
  return processedLines.join('');
};

/**
 * Render formatted content with React
 * Use dangerouslySetInnerHTML with the parsed content
 */
export const FormattedContent = ({ content, className = "" }) => {
  return (
    <div 
      className={`prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: parseSimpleMarkdown(content) }}
    />
  );
};

export default parseSimpleMarkdown;
